import { Button, Dialog, DialogContent, DialogTitle, Divider, Grid, Typography, useTheme } from "@mui/material";
import { connect, ConnectedProps } from "react-redux";
import { AnyAction, bindActionCreators, Dispatch } from "redux";
import { IStore } from "../../store/IStore";
import { DateTag } from "../atoms/DateTag";
import ImsEmbeddedTable, { ImsHeadCell, ImsTableRow } from "../atoms/ImsEmbeddedTable";
import { COLOR_GRAY_1 } from "../atoms/ImsMaterialTheme";
import { formatEuro, formatNumber } from "../atoms/Utils";
import NotificationList from "../notification/NotificationList";
import { getTotalBonusPoints } from "./Bonus";
import { formatBonusPoints } from "./KeyFigures";
import { formatCustomerName, useFormatInteractionCol, PaymentModel } from "./PaymentModel";
import { useTranslation } from "react-i18next";

interface BonusDialogProps extends ThunkProps {
  closeDialog: () => void;
  bonusPayments: PaymentModel[];
}

const BonusDialog = (props: BonusDialogProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const formatInteractionCol = useFormatInteractionCol();

  const headCells: ImsHeadCell[] = [
    {
      title: t("customerCareDetailsPage.bonusPointsHeadingDate"),
      align: "left",
      cellStyle: { verticalAlign: "top" },
    },
    {
      title: t("customerCareDetailsPage.bonusPointsHeadingInteraction"),
      align: "left",
      cellStyle: { verticalAlign: "top" },
    },
    {
      title: t("customerCareDetailsPage.bonusPointsHeadingMember"),
      align: "center",
      cellStyle: { verticalAlign: "top" },
    },
    {
      title: t("customerCareDetailsPage.bonusPointsHeadingAmount"),
      align: "center",
      cellStyle: { verticalAlign: "bottom" },
    },
    {
      title: t("customerCareDetailsPage.bonusPointsHeadingPoints"),
      align: "center",
      cellStyle: { verticalAlign: "bottom" },
    },
    {
      title: t("customerCareDetailsPage.bonusPointsHeadingNewTotal"),
      align: "center",
      cellStyle: { verticalAlign: "bottom" },
    },
  ];

  const rows: ImsTableRow[] = props.bonusPayments.map((row, index) => ({
    value: row,
    nodes: [
      <DateTag date={row.interactionTimestamp} />,
      formatInteractionCol(row),
      formatCustomerName(row.customerNumber, props.family),
      <>{formatEuro(row.displayedPaymentAmount || row.displayedCashValue)}</>,
      formatBonusPoints(row.bonusPoints),
      row.totalBonusPoints,
    ],
    key: index,
  }));

  return (
    <Dialog onClose={props.closeDialog} open={true} fullWidth={true} maxWidth="lg">
      <NotificationList isDialog />
      <DialogTitle style={{ padding: theme.spacing(3) }}>
        <Grid container>
          <Grid item md={6}>
            <b>{t("customerCareDetailsPage.bonusPointsHeadline")}</b>
          </Grid>
          <Grid item md={6}>
            <Typography align="right" variant="h2">
              {formatNumber(getTotalBonusPoints(props.bonusPayments))}
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <Divider style={{ color: COLOR_GRAY_1, height: "2px" }} light={true} />
      <DialogContent
        style={{
          padding: theme.spacing(0),
          overflow: "hidden",
          display: "flex",
        }}
      >
        {props.bonusPayments.length ? (
          <ImsEmbeddedTable headCells={headCells} rows={rows} />
        ) : (
          <p>{t("customerCareDetailsPage.bonusPointsNoDataFound")}</p>
        )}
      </DialogContent>
      <Divider style={{ color: COLOR_GRAY_1, height: "2px" }} light={true} />
      <Grid
        container
        justifyContent="flex-end"
        style={{
          padding: theme.spacing(2),
        }}
      >
        <Button
          id="btndone"
          color="primary"
          type="button"
          variant="contained"
          style={{ cursor: "pointer" }}
          onClick={props.closeDialog}
        >
          {t("customerCareDetailsPage.bonusPointsButtonDone")}
        </Button>
      </Grid>
    </Dialog>
  );
};

const mapStateToProps = (state: IStore) => ({
  family: state.customerCare.family,
});

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => bindActionCreators({}, dispatch);

const connector = connect(mapStateToProps, mapDispatchToProps);
type ThunkProps = ConnectedProps<typeof connector>;
export default connector(BonusDialog);
